import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { DialogContent } from '@mui/material';
import { BaseDialog } from '@resurs-internal/mui';
import { type ChangeEvent, useRef } from 'react';

import useTranslation from '../../hooks/useTranslation';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { ButtonContent, Input } from './UploadField.styles';

type Props = {
    open: boolean;
    setOpen: (open: boolean) => void;
    handleFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onSelectFile?: (file: File) => void;
    removeFile: () => void;
    file?: File | null;
    selectedFile?: File | null;
};

const DesktopUploadField = ({
    open,
    setOpen,
    handleFileChange,
    onSelectFile,
    removeFile,
    file,
    selectedFile,
}: Props) => {
    const translate = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    const noFileActions = [
        {
            onClick: () => {
                setOpen(false);
                removeFile();
            },
            title: translate('field.upload-document.dialog.cancel'),
        },
    ];

    const newFileActions = [
        {
            onClick: () => {
                setOpen(false);
                removeFile();
            },
            title: translate('field.upload-document.dialog.cancel'),
        },
        {
            onClick: () => {
                if (onSelectFile && file) {
                    onSelectFile(file);
                }
                setOpen(false);
            },
            title: translate('field.upload-document.dialog.upload'),
            color: 'primary',
        },
    ];

    const existingFileActions = [
        {
            onClick: () => {
                removeFile();
                setOpen(false);
            },
            title: translate('field.upload-document.dialog.delete'),
            color: 'secondary',
        },
        {
            onClick: () => inputRef.current?.click(),
            title: translate('field.upload-document.dialog.upload-new'),
            color: 'primary',
        },
    ];

    return (
        <BaseDialog
            open={open}
            onClose={() => setOpen(false)}
            maxWidth="sm"
            header={translate('field.upload-document.dialog.header')}
            actions={!file ? noFileActions : file !== selectedFile ? newFileActions : existingFileActions}>
            <DialogContent>
                <Input
                    type="file"
                    multiple={false}
                    ref={inputRef}
                    onChange={handleFileChange}
                    accept="image/png, image/jpeg, application/pdf"
                />
                {!file ? (
                    <Button variant="outlined" fill onClick={() => inputRef.current?.click()}>
                        <ButtonContent>
                            <Icon icon={faFileLines} size="xs" />{' '}
                            {translate('field.upload-document.dialog.choose-from-files')}
                        </ButtonContent>
                    </Button>
                ) : (
                    <>
                        <Icon icon={faFileLines} size="xl" /> {`${file?.name}`}
                    </>
                )}
            </DialogContent>
        </BaseDialog>
    );
};

export default DesktopUploadField;
